import React from "react";
import { Route } from "react-router-dom";
import { LazyLoadRoutes } from "routes/lazyLoadRoutes";

const VisibilityAvailability = React.lazy(() =>
  import(
    "features/ManageOfferings/modules/VisibilityAvailability/VisibilityAvailability"
  ).then((module) => ({
    default: module.VisibilityAvailability,
  }))
);

const PixelTracking = React.lazy(() =>
  import("features/ManageOfferings/modules/PixelTracking/PixelTracking").then(
    (module) => ({
      default: module.PixelTracking,
    })
  )
);

const OfferingPaymentOptions = React.lazy(() =>
  import(
    "features/ManageOfferings/modules/OfferingPaymentOptions/OfferingPaymentOptions"
  ).then((module) => ({
    default: module.OfferingPaymentOptions,
  }))
);

const Subscriptions = React.lazy(() =>
  import("features/ManageOfferings/modules/Subscriptions/Subscriptions").then(
    (module) => ({
      default: module.Subscriptions,
    })
  )
);

const OfferingPageSections = React.lazy(() =>
  import(
    "features/ManageOfferings/modules/OfferingPageSections/OfferingPageSections"
  ).then((module) => ({
    default: module.OfferingPageSections,
  }))
);

const MembershipsSection = React.lazy(() =>
  import(
    "features/ManageOfferings/modules/MembershipsSection/MembershipsSection"
  ).then((module) => ({
    default: module.MembershipsSection,
  }))
);

export const MANAGE_OFFERINGS_ROUTE_KEYS = {
  VISIBILITY_AVAILABILITY: "VISIBILITY_AVAILABILITY",
  PIXEL_TRACKING: "PIXEL_TRACKING",
  MEMBERSHIPS: "MEMBERSHIPS",
  OFFERING_FEEDBACK: "OFFERING_FEEDBACK",
  OFFERING_FEEDBACK_FORM: "OFFERING_FEEDBACK_FORM",
  OFFERING_PUBLISHED_FEEDBACK: "OFFERING_PUBLISHED_FEEDBACK",
  SUBSCRIPTIONS: "SUBSCRIPTIONS",
  PAGE_SECTIONS: "PAGE_SECTIONS",
  OFFERING_PAYMENT_OPTIONS: "OFFERING_PAYMENT_OPTIONS",
  FAQS: "FAQS",
  MANAGE_OFFERING: "manage_offering",
};

export const MANAGE_OFFERINGS_ROUTE_IDS = {
  [MANAGE_OFFERINGS_ROUTE_KEYS.VISIBILITY_AVAILABILITY]:
    "/visibility-availability/:listing_uuid",
  [MANAGE_OFFERINGS_ROUTE_KEYS.PIXEL_TRACKING]:
    "/purchase-pixel-tracking/:listing_uuid",
  [MANAGE_OFFERINGS_ROUTE_KEYS.MEMBERSHIPS]: "/memberships/:listing_uuid",
  [MANAGE_OFFERINGS_ROUTE_KEYS.OFFERING_FEEDBACK]:
    "offering-user-feedbacks/:listing_uuid",
  [MANAGE_OFFERINGS_ROUTE_KEYS.OFFERING_FEEDBACK_FORM]:
    "offering-feedback-form/:listing_uuid",
  [MANAGE_OFFERINGS_ROUTE_KEYS.OFFERING_PUBLISHED_FEEDBACK]:
    "offering-published-form/:listing_uuid",
  [MANAGE_OFFERINGS_ROUTE_KEYS.SUBSCRIPTIONS]: "/subscriptions",
  [MANAGE_OFFERINGS_ROUTE_KEYS.PAGE_SECTIONS]:
    "/offering-page-sections/:listing_uuid",
  [MANAGE_OFFERINGS_ROUTE_KEYS.FAQS]: "offering-faqs/:listing_uuid",
  [MANAGE_OFFERINGS_ROUTE_KEYS.OFFERING_PAYMENT_OPTIONS]:
    "offering-payment-options/:listing_uuid",
};

export const MANAGE_OFFERINGS_ROUTE_PATHNAMES = {
  [MANAGE_OFFERINGS_ROUTE_KEYS.OFFERING_PUBLISHED_FEEDBACK]: ({
    listing_uuid,
    section_key,
    navigate_back = false,
  }) =>
    `offering-published-form/${listing_uuid}?section_key=${section_key}&navigate_back=${navigate_back}`,
  [MANAGE_OFFERINGS_ROUTE_KEYS.OFFERING_FEEDBACK_FORM]: ({
    listing_uuid,
    section_key,
  }) => `offering-feedback-form/${listing_uuid}?section_key=${section_key}`,
  [MANAGE_OFFERINGS_ROUTE_KEYS.FAQS]: ({ listing_uuid }) =>
    `offering-faqs/${listing_uuid}`,
  [MANAGE_OFFERINGS_ROUTE_KEYS.OFFERING_PAYMENT_OPTIONS]: ({ listing_uuid }) =>
    `offering-payment-options/${listing_uuid}`,
  [MANAGE_OFFERINGS_ROUTE_KEYS.MEMBERSHIPS]: ({ listing_uuid, section_key }) =>
    `memberships/${listing_uuid}?section_key=${section_key}`,
  [MANAGE_OFFERINGS_ROUTE_KEYS.MANAGE_OFFERING]: ({
    listingUuid,
    utmParams = {},
  }) => {
    const basePath = `/manage-offering/${listingUuid}`;

    // Add UTM parameters if provided
    const utmString = new URLSearchParams(utmParams).toString();

    return utmString ? `${basePath}?${utmString}` : basePath;
  },
};

export const MANAGE_OFFERINGS_CUSTOM_ROUTES = [
  <Route
    exact
    key={
      MANAGE_OFFERINGS_ROUTE_IDS[
        MANAGE_OFFERINGS_ROUTE_KEYS.VISIBILITY_AVAILABILITY
      ]
    }
    path={
      MANAGE_OFFERINGS_ROUTE_IDS[
        MANAGE_OFFERINGS_ROUTE_KEYS.VISIBILITY_AVAILABILITY
      ]
    }
    render={(props) => (
      <LazyLoadRoutes LazyElement={VisibilityAvailability} props={props} />
    )}
  />,
  <Route
    exact
    key={MANAGE_OFFERINGS_ROUTE_IDS[MANAGE_OFFERINGS_ROUTE_KEYS.PIXEL_TRACKING]}
    path={
      MANAGE_OFFERINGS_ROUTE_IDS[MANAGE_OFFERINGS_ROUTE_KEYS.PIXEL_TRACKING]
    }
    render={(props) => (
      <LazyLoadRoutes LazyElement={PixelTracking} props={props} />
    )}
  />,
  <Route
    exact
    key={MANAGE_OFFERINGS_ROUTE_IDS[MANAGE_OFFERINGS_ROUTE_KEYS.PAGE_SECTIONS]}
    path={MANAGE_OFFERINGS_ROUTE_IDS[MANAGE_OFFERINGS_ROUTE_KEYS.PAGE_SECTIONS]}
    render={(props) => (
      <LazyLoadRoutes LazyElement={OfferingPageSections} props={props} />
    )}
  />,
  <Route
    exact
    key={`${
      MANAGE_OFFERINGS_ROUTE_IDS[MANAGE_OFFERINGS_ROUTE_KEYS.SUBSCRIPTIONS]
    }/:listing_uuid`}
    path={`${
      MANAGE_OFFERINGS_ROUTE_IDS[MANAGE_OFFERINGS_ROUTE_KEYS.SUBSCRIPTIONS]
    }/:listing_uuid`}
    render={(props) => (
      <LazyLoadRoutes LazyElement={Subscriptions} props={props} />
    )}
  />,
  <Route
    exact
    key={`/${
      MANAGE_OFFERINGS_ROUTE_IDS[
        MANAGE_OFFERINGS_ROUTE_KEYS.OFFERING_PAYMENT_OPTIONS
      ]
    }`}
    path={`/${
      MANAGE_OFFERINGS_ROUTE_IDS[
        MANAGE_OFFERINGS_ROUTE_KEYS.OFFERING_PAYMENT_OPTIONS
      ]
    }`}
    render={(props) => (
      <LazyLoadRoutes LazyElement={OfferingPaymentOptions} props={props} />
    )}
  />,
  <Route
    exact
    key={MANAGE_OFFERINGS_ROUTE_IDS[MANAGE_OFFERINGS_ROUTE_KEYS.MEMBERSHIPS]}
    path={MANAGE_OFFERINGS_ROUTE_IDS[MANAGE_OFFERINGS_ROUTE_KEYS.MEMBERSHIPS]}
    render={(props) => (
      <LazyLoadRoutes LazyElement={MembershipsSection} props={props} />
    )}
  />,
];
